import { useEffect, useRef, useState } from "react";

const MIN_SCALE = 1;
const DEFAULT_MAX_SCALE = 4;
const DOUBLE_TAP_SCALE = 2.5;
const DOUBLE_TAP_DELAY = 300;
const ZOOM_STEP = 1.5;

function clamp(value, min, max) {
    return Math.max(min, Math.min(max, value));
}

function getMidpoint(touches) {
    return {
        x: (touches[0].clientX + touches[1].clientX) / 2,
        y: (touches[0].clientY + touches[1].clientY) / 2,
    };
}

function getDistance(touches) {
    const dx = touches[0].clientX - touches[1].clientX;
    const dy = touches[0].clientY - touches[1].clientY;
    return Math.hypot(dx, dy);
}

const ZoomablePreview = ({ src }) => {
    const containerRef = useRef(null);
    const imgRef = useRef(null);
    const zoomInBtnRef = useRef(null);
    const zoomOutBtnRef = useRef(null);
    const animTimeoutRef = useRef(null);
    const g = useRef({
        scale: 1,
        tx: 0,
        ty: 0,
        maxScale: DEFAULT_MAX_SCALE,
        mode: "none",
        lastTapAt: 0,
        lastTouchDoubleTapAt: 0,
        lastTouchTime: 0,
    }).current;
    const [zoomBtns, setZoomBtns] = useState({
        canZoomIn: true,
        canZoomOut: false,
    });

    const applyTransform = (animate = false) => {
        const img = imgRef.current;
        if (!img) return;
        if (animate) {
            img.style.transition =
                "transform 220ms cubic-bezier(0.4, 0, 0.2, 1)";
            clearTimeout(animTimeoutRef.current);
            animTimeoutRef.current = setTimeout(() => {
                img.style.transition = "none";
            }, 240);
        } else {
            img.style.transition = "none";
        }
        img.style.transform = `translate3d(${g.tx}px, ${g.ty}px, 0) scale(${g.scale})`;
        const canZoomIn = g.scale < g.maxScale - 0.001;
        const canZoomOut = g.scale > MIN_SCALE + 0.001;
        setZoomBtns((prev) =>
            prev.canZoomIn === canZoomIn && prev.canZoomOut === canZoomOut
                ? prev
                : { canZoomIn, canZoomOut }
        );
    };

    const clampTranslate = () => {
        const el = containerRef.current;
        if (!el) return;
        const maxTx = (el.clientWidth * (g.scale - 1)) / 2;
        const maxTy = (el.clientHeight * (g.scale - 1)) / 2;
        g.tx = clamp(g.tx, -maxTx, maxTx);
        g.ty = clamp(g.ty, -maxTy, maxTy);
    };

    const updateMaxScale = () => {
        const img = imgRef.current;
        const el = containerRef.current;
        if (!img || !el) return;
        const naturalW = img.naturalWidth;
        const naturalH = img.naturalHeight;
        if (!naturalW || !naturalH) return;
        const maxScale = Math.max(
            MIN_SCALE,
            Math.max(naturalW / el.clientWidth, naturalH / el.clientHeight)
        );
        if (maxScale === g.maxScale) return;
        g.maxScale = maxScale;
        if (g.scale > maxScale) {
            g.scale = maxScale;
            clampTranslate();
        }
        applyTransform();
    };

    const resetOverlay = () => {
        const el = containerRef.current;
        if (!el) return;
        const overlay = el.closest(".Modal__overlay");
        if (overlay) {
            overlay.style.background = "";
        }
    };

    const drag = (dx, dy) => {
        if (g.scale <= MIN_SCALE) return;
        g.tx = g.startTx + dx;
        g.ty = g.startTy + dy;
        clampTranslate();
        applyTransform();
    };

    const beginDrag = (x, y, mode) => {
        g.mode = mode;
        g.startX = x;
        g.startY = y;
        g.startTx = g.tx;
        g.startTy = g.ty;
    };

    const endPan = () => {
        if (g.scale > MIN_SCALE) {
            clampTranslate();
            applyTransform(true);
        }
    };

    const toggleZoom = (clientX, clientY) => {
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const px = clientX - rect.left;
        const py = clientY - rect.top;
        const cw = rect.width;
        const ch = rect.height;
        if (g.scale > 1) {
            g.scale = 1;
            g.tx = 0;
            g.ty = 0;
            resetOverlay();
        } else {
            const target = Math.min(DOUBLE_TAP_SCALE, g.maxScale);
            if (target <= MIN_SCALE + 0.001) return;
            g.scale = target;
            g.tx = (px - cw / 2) * (1 - target);
            g.ty = (py - ch / 2) * (1 - target);
            clampTranslate();
        }
        applyTransform(true);
    };

    const zoomBy = (factor) => {
        const oldScale = g.scale;
        const newScale = clamp(oldScale * factor, MIN_SCALE, g.maxScale);
        if (newScale === oldScale) return;
        g.scale = newScale;
        g.tx *= newScale / oldScale;
        g.ty *= newScale / oldScale;
        clampTranslate();
        applyTransform(true);
    };

    const zoomIn = () => zoomBy(ZOOM_STEP);
    const zoomOut = () => zoomBy(1 / ZOOM_STEP);

    const onTouchStart = (e) => {
        if (e.target.closest(".PreviewModal__zoom-btn")) return;
        g.lastTouchTime = Date.now();
        if (e.touches.length === 2) {
            g.mode = "pinch";
            g.startDist = getDistance(e.touches);
            g.startScale = g.scale;
            const mid = getMidpoint(e.touches);
            g.startMidX = mid.x;
            g.startMidY = mid.y;
            g.startTx = g.tx;
            g.startTy = g.ty;
            return;
        }
        if (e.touches.length === 1) {
            const touch = e.touches[0];
            const now = Date.now();
            if (now - g.lastTapAt < DOUBLE_TAP_DELAY) {
                g.lastTapAt = 0;
                g.lastTouchDoubleTapAt = now;
                toggleZoom(touch.clientX, touch.clientY);
                return;
            }
            g.lastTapAt = now;
            beginDrag(touch.clientX, touch.clientY, "pan");
        }
    };

    const onTouchMove = (e) => {
        if (e.target.closest(".PreviewModal__zoom-btn")) return;
        if (e.touches.length === 2 && g.mode === "pinch") {
            e.preventDefault();
            const el = containerRef.current;
            if (!el) return;
            const rect = el.getBoundingClientRect();
            const dist = getDistance(e.touches);
            const mid = getMidpoint(e.touches);
            const scale = clamp(
                g.startScale * (dist / g.startDist),
                MIN_SCALE,
                g.maxScale
            );
            g.scale = scale;
            g.tx =
                g.startTx +
                (g.startMidX - rect.left - rect.width / 2) * g.startScale -
                (mid.x - rect.left - rect.width / 2) * scale;
            g.ty =
                g.startTy +
                (g.startMidY - rect.top - rect.height / 2) * g.startScale -
                (mid.y - rect.top - rect.height / 2) * scale;
            clampTranslate();
            applyTransform();
            return;
        }
        if (e.touches.length === 1 && g.mode === "pan") {
            e.preventDefault();
            const touch = e.touches[0];
            drag(touch.clientX - g.startX, touch.clientY - g.startY);
        }
    };

    const onTouchEnd = (e) => {
        g.lastTouchTime = Date.now();
        if (g.mode === "pinch") {
            if (e.touches.length === 1) {
                const touch = e.touches[0];
                beginDrag(touch.clientX, touch.clientY, "pan");
                return;
            }
            if (e.touches.length === 0) {
                g.mode = "none";
                endPan();
            }
            return;
        }
        if (g.mode === "pan" && e.touches.length === 0) {
            g.mode = "none";
            endPan();
        }
    };

    const onWheel = (e) => {
        if (e.target.closest(".PreviewModal__zoom-btn")) return;
        e.preventDefault();
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        const px = e.clientX - rect.left;
        const py = e.clientY - rect.top;
        const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
        const scale = clamp(g.scale * factor, MIN_SCALE, g.maxScale);
        if (scale === g.scale) return;
        g.tx += (px - rect.width / 2) * (g.scale - scale);
        g.ty += (py - rect.height / 2) * (g.scale - scale);
        g.scale = scale;
        clampTranslate();
        applyTransform();
    };

    const onDoubleClick = (e) => {
        if (e.target.closest(".PreviewModal__zoom-btn")) return;
        if (Date.now() - g.lastTouchDoubleTapAt < 500) return;
        toggleZoom(e.clientX, e.clientY);
    };

    const onMouseDown = (e) => {
        if (e.button !== 0) return;
        if (e.target.closest(".PreviewModal__zoom-btn")) return;
        if (Date.now() - g.lastTouchTime < 500) return;
        e.preventDefault();
        beginDrag(e.clientX, e.clientY, "mouse");
        containerRef.current.classList.add("is-dragging");
    };

    const onMouseMove = (e) => {
        if (g.mode !== "mouse") return;
        drag(e.clientX - g.startX, e.clientY - g.startY);
    };

    const onMouseUp = (e) => {
        if (g.mode !== "mouse") return;
        g.mode = "none";
        containerRef.current.classList.remove("is-dragging");
        endPan();
    };

    useEffect(() => {
        const el = containerRef.current;
        const zoomInBtn = zoomInBtnRef.current;
        const zoomOutBtn = zoomOutBtnRef.current;
        const stopDoubleClick = (e) => e.stopPropagation();
        el.addEventListener("touchstart", onTouchStart, { passive: false });
        el.addEventListener("touchmove", onTouchMove, { passive: false });
        el.addEventListener("touchend", onTouchEnd, { passive: false });
        el.addEventListener("touchcancel", onTouchEnd, { passive: false });
        el.addEventListener("wheel", onWheel, { passive: false });
        el.addEventListener("dblclick", onDoubleClick);
        el.addEventListener("mousedown", onMouseDown);
        window.addEventListener("mousemove", onMouseMove);
        window.addEventListener("mouseup", onMouseUp);
        window.addEventListener("resize", updateMaxScale);
        zoomInBtn.addEventListener("dblclick", stopDoubleClick);
        zoomOutBtn.addEventListener("dblclick", stopDoubleClick);
        if (imgRef.current && imgRef.current.complete) {
            updateMaxScale();
        }
        return () => {
            clearTimeout(animTimeoutRef.current);
            el.removeEventListener("touchstart", onTouchStart);
            el.removeEventListener("touchmove", onTouchMove);
            el.removeEventListener("touchend", onTouchEnd);
            el.removeEventListener("touchcancel", onTouchEnd);
            el.removeEventListener("wheel", onWheel);
            el.removeEventListener("dblclick", onDoubleClick);
            el.removeEventListener("mousedown", onMouseDown);
            window.removeEventListener("mousemove", onMouseMove);
            window.removeEventListener("mouseup", onMouseUp);
            window.removeEventListener("resize", updateMaxScale);
            zoomInBtn.removeEventListener("dblclick", stopDoubleClick);
            zoomOutBtn.removeEventListener("dblclick", stopDoubleClick);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="PreviewModal__zoom" ref={containerRef}>
            <img
                ref={imgRef}
                src={src}
                alt=""
                draggable={false}
                onLoad={updateMaxScale}
            />
            <div className="PreviewModal__zoom-controls">
                <button
                    ref={zoomInBtnRef}
                    type="button"
                    className="PreviewModal__zoom-btn"
                    onClick={zoomIn}
                    disabled={!zoomBtns.canZoomIn}
                    aria-label="Zoom in"
                    title="Zoom in"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="20" height="20">
                        <path fill="currentColor" d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376C296.3 401.1 253.9 416 208 416 93.1 416 0 322.9 0 208S93.1 0 208 0 416 93.1 416 208zM208 112c-13.3 0-24 10.7-24 24l0 48-48 0c-13.3 0-24 10.7-24 24s10.7 24 24 24l48 0 0 48c0 13.3 10.7 24 24 24s24-10.7 24-24l0-48 48 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-48 0 0-48c0-13.3-10.7-24-24-24z" />
                    </svg>
                </button>
                <button
                    ref={zoomOutBtnRef}
                    type="button"
                    className="PreviewModal__zoom-btn"
                    onClick={zoomOut}
                    disabled={!zoomBtns.canZoomOut}
                    aria-label="Zoom out"
                    title="Zoom out"
                >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="20" height="20">
                        <path fill="currentColor" d="M416 208c0 45.9-14.9 88.3-40 122.7L502.6 457.4c12.5 12.5 12.5 32.8 0 45.3s-32.8 12.5-45.3 0L330.7 376C296.3 401.1 253.9 416 208 416 93.1 416 0 322.9 0 208S93.1 0 208 0 416 93.1 416 208zM136 184c-13.3 0-24 10.7-24 24s10.7 24 24 24l144 0c13.3 0 24-10.7 24-24s-10.7-24-24-24l-144 0z" />
                    </svg>
                </button>
            </div>
        </div>
    );
};

ZoomablePreview.displayName = "ZoomablePreview";

export default ZoomablePreview;
